import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, User, Heart, MessageCircle, Menu, X, MapPin, Gavel, LogOut, Crown, Building2 } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CASTELLI } from "../constants";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";
import MobileMenu from "./MobileMenu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Header() {
  const [query, setQuery] = useState("");
  const [castello, setCastello] = useState("Tutti i Castelli");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (castello && castello !== "Tutti i Castelli") params.set("castello", castello);
    navigate(`/cerca?${params.toString()}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
      {/* Top bar */}
      <div className="bg-[#1d3d3a] text-[#f5ebd9] text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3" />
            <span>Esclusivo per la Repubblica di San Marino</span>
          </div>
          <div className="hidden sm:flex gap-4">
            <Link to="/aste" className="hover:text-white flex items-center gap-1"><Gavel className="w-3 h-3"/>Aste Live</Link>
            <span>Assistenza</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        {/* Logo */}
        <Link to="/" className="shrink-0">
          <Logo size={44} />
        </Link>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex-1 hidden md:flex items-center rounded-full border border-slate-300 overflow-hidden bg-white focus-within:border-[#e86a24] transition-colors">
          <div className="flex-1 flex items-center px-4">
            <Search className="w-4 h-4 text-slate-400 mr-2" />
            <input
              type="text"
              placeholder="Cosa stai cercando?"
              className="flex-1 outline-none py-2 text-sm bg-transparent"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="border-l border-slate-200">
            <Select value={castello} onValueChange={setCastello}>
              <SelectTrigger className="border-0 rounded-none h-10 w-[180px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CASTELLI.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button type="submit" className="bg-[#e86a24] hover:bg-[#c8561a] text-white px-5 h-10 text-sm font-semibold transition-colors">
            Cerca
          </button>
        </form>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <Link to="/pubblica">
            <Button className="bg-[#e86a24] hover:bg-[#c8561a] text-white rounded-full shadow-sm">
              <Plus className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Vendi ora</span>
              <span className="sm:hidden">Vendi</span>
            </Button>
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-slate-200 px-2 py-1 hover:border-slate-400 transition-colors">
                  <img src={user.avatar || "https://i.pravatar.cc/80"} alt="avatar" className="w-7 h-7 rounded-full" />
                  <span className="hidden md:inline text-sm font-medium text-slate-700">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/profilo")}><User className="w-4 h-4 mr-2" />Il mio profilo</DropdownMenuItem>
                {user.account_type === "business" && (
                  <DropdownMenuItem onClick={() => navigate("/azienda/dashboard")} className="text-amber-700 font-semibold" data-testid="header-business-dashboard">
                    <Building2 className="w-4 h-4 mr-2" />Dashboard Azienda
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem><Heart className="w-4 h-4 mr-2" />I miei preferiti</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/messaggi")}><MessageCircle className="w-4 h-4 mr-2" />Messaggi</DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="text-[#e86a24] font-semibold">
                    <Crown className="w-4 h-4 mr-2" />Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600"><LogOut className="w-4 h-4 mr-2" />Esci</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/login"><Button variant="ghost" className="rounded-full text-[#1d3d3a]">Accedi</Button></Link>
              <Link to="/scegli-account"><Button className="rounded-full bg-[#e86a24] hover:bg-[#c8561a]" data-testid="header-register-button">Registrati</Button></Link>
            </div>
          )}

          <button className="md:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setMobileOpen(true)} aria-label="Menu" data-testid="mobile-menu-trigger">
            <Menu className="w-6 h-6 text-[#1d3d3a]" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Mobile search */}
      <div className="md:hidden px-4 pb-3">
        <form onSubmit={handleSearch} className="flex items-center rounded-full border border-slate-300 bg-white">
          <Search className="w-4 h-4 text-slate-400 ml-3" />
          <input
            type="text"
            placeholder="Cerca..."
            className="flex-1 outline-none py-2 px-2 text-sm bg-transparent"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" className="bg-[#e86a24] text-white px-4 py-2 rounded-full m-0.5 text-sm">Cerca</button>
        </form>
      </div>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}
